/**
 * B2B Clients - the client master (FRD §3.1).
 *
 * Every PG / hostel / society billed as one account. Configured ONCE: its rooms,
 * rate card, cadence and contact are stored so an operator never retypes them.
 *
 * Columns, labels and the empty state follow the design source
 * (B2B/src/app/components/rapidry/B2BClientMaster.tsx → `ClientList`):
 * Client · Client Type · Rooms · Billing Cycle · POC · Status · Actions.
 *
 * Search, filtering and paging are SERVER-side - a client master can run to
 * hundreds of accounts, and filtering a partial page in the browser would show
 * the operator the wrong answer with no indication it was wrong.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Building2, MoreHorizontal, Pencil, Plus, RefreshCw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { useColors } from '../../hooks/useColors'
import EmptyState from '../../components/shared/EmptyState'
import LoadErrorState from '../../components/common/LoadErrorState'
import { withServerTimeout } from '../../components/common/loadErrorUtils'
import {
  Card,
  Field,
  PageHeader,
  PillButton,
  SearchInput,
  SelectInput,
  StatCard,
  StatRow,
  TableHead,
  Td,
} from '../../features/b2b/components/primitives'
import ClientStatusBadge from '../../features/b2b/components/ClientStatusBadge'
import ClientFormModal from './ClientFormModal'
import { formatMoney } from '../../features/b2b/format'
import { b2bApi } from '../../services/b2bApi'
import type { B2bClientListItem, B2bClientStatus } from '../../services/b2bApi'
import type { B2bClientType, BillingCycleLength } from '../../features/b2b/types'

const TYPE_LABELS: Record<B2bClientType, string> = {
  pg: 'PG',
  hostel: 'Hostel',
  society: 'Society',
  corporate: 'Corporate',
  other: 'Other',
}

const CYCLE_LABELS: Record<BillingCycleLength, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
}

const STATUS_OPTIONS: Array<{ value: B2bClientStatus; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'paused', label: 'Paused' },
]

const PAGE_SIZE = 20

export default function B2bClientsPage() {
  const colors = useColors()
  const navigate = useNavigate()

  const [clients, setClients] = useState<B2bClientListItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [type, setType] = useState('')
  const [status, setStatus] = useState('')
  const [showCreate, setShowCreate] = useState(false)

  // Typing must not fire a request per keystroke against a table this size.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, type, status])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await withServerTimeout(
        b2bApi.listClients({
          search: debouncedSearch || undefined,
          client_type: (type || undefined) as B2bClientType | undefined,
          status: (status || undefined) as B2bClientStatus | undefined,
          page,
          limit: PAGE_SIZE,
        }),
      )
      setClients(data.clients)
      setTotal(data.total)
    } catch (err) {
      setError(
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ||
          (err as Error)?.message ||
          'Could not load B2B clients.',
      )
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, page, status, type])

  useEffect(() => {
    void load()
  }, [load])

  const hasFilters = Boolean(debouncedSearch || type || status)

  // Page-level rollups. Deliberately labelled "on this page" - the server pages
  // the list, so summing what is loaded is not the whole book and must not
  // pretend to be.
  const totals = useMemo(
    () =>
      clients.reduce(
        (acc, client) => ({
          rooms: acc.rooms + client.room_count,
          billed: acc.billed + (client.open_cycle?.rooms_billed || 0),
          value: acc.value + (client.open_cycle?.total_amount || 0),
        }),
        { rooms: 0, billed: 0, value: 0 },
      ),
    [clients],
  )

  const startCount = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  // Count what actually came back, not what a full page would hold - a short
  // final page must not claim to be showing rows that are not on screen.
  const endCount = (page - 1) * PAGE_SIZE + clients.length
  const canGoPrev = page > 1
  const canGoNext = endCount < total

  const clearFilters = () => {
    setSearch('')
    setType('')
    setStatus('')
  }

  return (
    <div>
      <PageHeader
        title="Client master"
        subtitle="One-time configuration for B2B client billing."
        actions={
          <PillButton onClick={() => setShowCreate(true)}>
            <Plus size={16} /> Create client
          </PillButton>
        }
      />

      <StatRow>
        <StatCard label="Clients" value={String(total)} hint={hasFilters ? 'matching filters' : undefined} />
        <StatCard label="Rooms on this page" value={String(totals.rooms)} />
        <StatCard label="Rooms billed this cycle" value={String(totals.billed)} hint={`of ${totals.rooms} rooms`} />
        <StatCard label="Open cycle value" value={formatMoney(totals.value)} color="var(--chip-success-ink)" />
      </StatRow>

      <Card section>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'flex-end',
            marginBottom: '16px',
          }}
        >
          <Field label="Search">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search client, POC or address"
              ariaLabel="Search clients"
              width="280px"
            />
          </Field>
          <Field label="Client Type">
            <SelectInput
              value={type}
              aria-label="Client type"
              onChange={(event) => setType(event.target.value)}
              style={{ minWidth: '150px' }}
            >
              <option value="">All types</option>
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Status">
            <SelectInput
              value={status}
              aria-label="Status"
              onChange={(event) => setStatus(event.target.value)}
              style={{ minWidth: '150px' }}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
          </Field>
          {hasFilters ? (
            <PillButton variant="secondary" onClick={clearFilters}>
              Clear filters
            </PillButton>
          ) : null}
          <PillButton variant="secondary" onClick={() => void load()} title="Refresh">
            <RefreshCw size={15} /> Refresh
          </PillButton>
        </div>

        {error && !loading ? (
          <LoadErrorState message={error} onRetry={() => void load()} />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <TableHead
                  columns={[
                    { key: 'client', label: 'Client' },
                    { key: 'type', label: 'Client Type' },
                    { key: 'rooms', label: 'Rooms' },
                    { key: 'cycle', label: 'Billing Cycle' },
                    { key: 'poc', label: 'POC' },
                    { key: 'status', label: 'Status' },
                    { key: 'actions', label: 'Actions' },
                  ]}
                />
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <div style={{ padding: '18px', display: 'grid', gap: '10px' }}>
                          {Array.from({ length: 5 }).map((_, index) => (
                            <div
                              key={index}
                              className="rapidry-pulse"
                              style={{ height: '44px', borderRadius: '10px', background: colors.surface }}
                            />
                          ))}
                        </div>
                      </td>
                    </tr>
                  ) : clients.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <EmptyState
                            icon={Building2}
                            title={hasFilters ? 'No clients match those filters' : 'No B2B clients found'}
                            description={
                              hasFilters
                                ? 'Try a different name, type or status.'
                                : 'Create a client once to save their rooms, billing cycle, rate card and contact for future billing.'
                            }
                          />
                          {hasFilters ? (
                            <PillButton variant="secondary" onClick={clearFilters}>
                              Clear filters
                            </PillButton>
                          ) : (
                            <PillButton onClick={() => setShowCreate(true)}>
                              <Plus size={16} /> Create client
                            </PillButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    clients.map((client) => (
                      <tr
                        key={client.id}
                        onClick={() => navigate(`/b2b/clients/${client.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Td strong>
                          <div>{client.name}</div>
                          <div style={{ fontSize: '11px', color: colors.textMuted, fontWeight: 400 }}>
                            {client.address}
                          </div>
                        </Td>
                        <Td muted>{TYPE_LABELS[client.client_type] ?? client.client_type}</Td>
                        <Td strong>{client.room_count}</Td>
                        <Td muted>
                          {CYCLE_LABELS[client.billing_cycle_length] ?? client.billing_cycle_length}
                        </Td>
                        <Td muted>
                          <div style={{ fontWeight: 600, color: colors.textSecondary }}>
                            {client.poc_name || '-'}
                          </div>
                          <div style={{ fontSize: '12px' }}>{client.poc_phone || 'No phone on file'}</div>
                        </Td>
                        <Td>
                          <ClientStatusBadge status={client.status} />
                        </Td>
                        <Td>
                          <div
                            style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <PillButton
                              variant="secondary"
                              onClick={() => navigate(`/b2b/billing?client=${client.id}`)}
                            >
                              <Pencil size={13} /> Open billing
                            </PillButton>
                            <button
                              type="button"
                              aria-label={`Details for ${client.name}`}
                              onClick={() => navigate(`/b2b/clients/${client.id}`)}
                              style={{
                                display: 'grid',
                                placeItems: 'center',
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                border: 'none',
                                background: 'transparent',
                                color: colors.textMuted,
                                cursor: 'pointer',
                              }}
                            >
                              <MoreHorizontal size={17} />
                            </button>
                          </div>
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {total > PAGE_SIZE ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  marginTop: '14px',
                }}
              >
                <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>
                  Showing {startCount}-{endCount} of {total} clients
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <PillButton
                    variant="secondary"
                    disabled={!canGoPrev}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    Previous
                  </PillButton>
                  <PillButton
                    variant="secondary"
                    disabled={!canGoNext}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Next
                  </PillButton>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>

      {showCreate ? (
        <ClientFormModal
          onClose={() => setShowCreate(false)}
          onSaved={(clientId) => {
            setShowCreate(false)
            navigate(`/b2b/clients/${clientId}`)
          }}
        />
      ) : null}
    </div>
  )
}
